// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Archive, Check, Copy, ExternalLink, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { useArchiveDeal, usePatchDeal } from "@/hooks/useDeals";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

export default function ShareTab({ deal, onUpdated }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const shareUrl = `${window.location.origin}/share/${deal.share_slug}`;

  const { mutateAsync: patchDealAsync } = usePatchDeal();
  const { mutateAsync: archiveDeal } = useArchiveDeal();

  const toggleShare = async () => {
    const res = await patchDealAsync({ dealId: deal.id, payload: { share_enabled: !deal.share_enabled } });
    onUpdated(res.data);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const archive = async () => {
    await archiveDeal(deal.id);
    navigate("/dashboard");
  };

  return (
    <div className="flex max-w-3xl flex-col gap-5" data-testid="share-tab">
      <Card className="rounded-2xl border-dash-border shadow-none">
        <CardContent className="flex flex-col gap-5 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-5">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-dash-faint">
                <Globe2 className="size-4 text-dash-ink" /> Share link
              </div>
              <h3 className="mt-2 text-base font-semibold text-dash-ink">A read-only page for your client.</h3>
              <p className="mt-1 text-sm leading-6 text-dash-muted">
                Anyone with the link can follow this deal. No login required.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Switch
                checked={Boolean(deal.share_enabled)}
                onCheckedChange={toggleShare}
                aria-label="Enable share link"
                data-testid="share-toggle"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="min-w-0 flex-1 truncate rounded-xl bg-dash-page px-3 py-2.5 text-xs text-dash-muted" data-testid="share-url">
              {shareUrl}
            </code>
            <div className="flex shrink-0 items-center gap-2">
              <Button type="button" variant="dashboardPrimary" onClick={copy} data-testid="share-copy-btn">
                {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
                {copied ? "Copied" : "Copy link"}
              </Button>
              <Button type="button" variant="dashboardOutline" asChild>
                <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="share-open-btn">
                  <ExternalLink data-icon="inline-start" /> Open
                </a>
              </Button>
            </div>
          </div>

          {!deal.share_enabled && (
            <p className="text-xs text-dash-faint">
              The link is currently off — turn it on before sending it out.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-destructive/25 shadow-none">
        <CardContent className="flex flex-col justify-between gap-5 p-5 sm:flex-row sm:items-center sm:p-6">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-destructive">
              <AlertTriangle className="size-4" /> Danger zone
            </div>
            <h3 className="mt-4 text-sm font-semibold text-dash-ink">Archive this deal.</h3>
            <p className="mt-1 text-xs leading-5 text-dash-muted">
              Archived deals are removed from your dashboard and stop counting toward your plan’s active-deal limit. All history is preserved — you can restore any time. Permanent deletion is only available after archiving.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmArchive(true)}
            className="shrink-0 border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
            data-testid="archive-deal"
          >
            <Archive data-icon="inline-start" /> Archive deal
          </Button>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmArchive}
        onOpenChange={setConfirmArchive}
        title="Archive this deal?"
        description="It will be removed from the active dashboard, but all deal history will be preserved and it can be restored later."
        confirmLabel="Archive deal"
        busyLabel="Archiving…"
        onConfirm={archive}
        testId="archive-deal-modal"
      />
    </div>
  );
}
